import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../../components/Layout';
import { toast } from 'react-toastify';
import { get, post, put, del } from '../../controllers/apiController';
import { useSettings } from '../../context/SettingsContext';

// ─── Renk seçenekleri ─────────────────────────────────────────────────────────
const COLORS = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
    '#06b6d4', '#ec4899', '#f97316', '#14b8a6', '#6366f1'
];

// ─── Modül kartı bileşeni ──────────────────────────────────────────────────────
const ModuleCard = ({ icon, iconBg, title, description, active, onToggle, children }) => (
    <div style={{
        background: '#fff', borderRadius: '14px', border: `2px solid ${active ? '#10b981' : '#e5e7eb'}`,
        padding: '20px', marginBottom: '16px', transition: 'border-color 0.2s'
    }}>
        <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center gap-3">
                <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className={icon} style={{ fontSize: '20px', color: '#fff' }}></i>
                </div>
                <div>
                    <div style={{ fontWeight: 700, fontSize: '15px', color: '#111' }}>{title}</div>
                    <div style={{ fontSize: '12px', color: '#888' }}>{description}</div>
                </div>
            </div>
            {/* Toggle switch */}
            <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', userSelect: 'none' }}>
                <span style={{ fontSize: '12px', color: active ? '#10b981' : '#aaa', fontWeight: 600 }}>
                    {active ? 'Aktif' : 'Pasif'}
                </span>
                <div
                    onClick={onToggle}
                    style={{
                        width: '46px', height: '24px', borderRadius: '12px',
                        background: active ? '#10b981' : '#d1d5db',
                        position: 'relative', transition: 'background 0.2s', cursor: 'pointer'
                    }}>
                    <div style={{
                        width: '18px', height: '18px', borderRadius: '50%', background: '#fff',
                        position: 'absolute', top: '3px',
                        left: active ? '25px' : '3px',
                        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                    }} />
                </div>
            </label>
        </div>
        {active && children && (
            <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '16px' }}>
                {children}
            </div>
        )}
    </div>
);

// ─── Form alanı bileşeni ───────────────────────────────────────────────────────
const Field = ({ label, type = 'text', value, onChange, placeholder, help, password }) => {
    const [show, setShow] = useState(false);
    return (
        <div className="mb-3">
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>{label}</label>
            <div style={{ position: 'relative' }}>
                <input
                    type={password && !show ? 'password' : type}
                    className="form-control form-control-sm"
                    value={value || ''}
                    onChange={e => onChange(e.target.value)}
                    placeholder={placeholder}
                    style={{ borderRadius: '8px', fontSize: '13px', paddingRight: password ? '36px' : undefined }}
                />
                {password && (
                    <button type="button" onClick={() => setShow(s => !s)}
                        style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#888', padding: 0 }}>
                        <i className={`feather-${show ? 'eye-off' : 'eye'}`} style={{ fontSize: '14px' }}></i>
                    </button>
                )}
            </div>
            {help && <div style={{ fontSize: '11px', color: '#aaa', marginTop: '3px' }}>{help}</div>}
        </div>
    );
};

// ─── Tur renk/etiket yardımcıları ─────────────────────────────────────────────
const TUR_OPTIONS = [
    { value: 'normal',     label: 'Normal',           color: '#6b7280' },
    { value: 'teklif',     label: 'Teklif',           color: '#8b5cf6' },
    { value: 'kazanildi',  label: 'Kazanıldı',        color: '#10b981' },
    { value: 'kaybedildi', label: 'Kaybedildi',       color: '#ef4444' },
];
const turInfo = (tur) => TUR_OPTIONS.find(t => t.value === tur) || TUR_OPTIONS[0];

// ─── Pipeline Aşama Satırı ─────────────────────────────────────────────────────
const StageRow = ({ stage, onSave, onDelete, onMoveUp, onMoveDown, isFirst, isLast }) => {
    const [editing, setEditing] = useState(false);
    const [ad, setAd] = useState(stage.Ad);
    const [renk, setRenk] = useState(stage.Renk || '#3b82f6');
    const [tur, setTur] = useState(stage.Tur || 'normal');
    const [hatirlatmaGun, setHatirlatmaGun] = useState(stage.Hatirlatma_Gun || '');
    const [kapanmaYuzdesi, setKapanmaYuzdesi] = useState(stage.Kapanma_Yuzdesi ?? 0);
    const [altAksiyonlarInput, setAltAksiyonlarInput] = useState('');
    const [altAksiyonlar, setAltAksiyonlar] = useState(() => {
        try { return stage.Alt_Aksiyonlar ? JSON.parse(stage.Alt_Aksiyonlar) : []; } catch { return []; }
    });
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        await onSave(stage.Asama_ID, {
            ad, renk, sira: stage.Sira, aktif: stage.Aktif,
            tur, hatirlatma_gun: hatirlatmaGun ? parseInt(hatirlatmaGun) : null,
            alt_aksiyonlar: altAksiyonlar.length ? altAksiyonlar : null,
            kapanma_yuzdesi: kapanmaYuzdesi !== '' ? parseInt(kapanmaYuzdesi) : 0
        });
        setEditing(false);
        setSaving(false);
    };

    const addAltAksiyon = () => {
        const v = altAksiyonlarInput.trim();
        if (v && !altAksiyonlar.includes(v)) setAltAksiyonlar(prev => [...prev, v]);
        setAltAksiyonlarInput('');
    };
    const removeAltAksiyon = (v) => setAltAksiyonlar(prev => prev.filter(x => x !== v));

    const ti = turInfo(stage.Tur);
    const stageAltList = (() => { try { return stage.Alt_Aksiyonlar ? JSON.parse(stage.Alt_Aksiyonlar) : []; } catch { return []; } })();

    return (
        <div style={{
            background: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb', marginBottom: '8px',
            overflow: 'hidden'
        }}>
            {/* ── Üst satır (özet) ── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px' }}>
                {/* Sıralama okları */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', flexShrink: 0 }}>
                    <button onClick={() => onMoveUp(stage.Asama_ID)} disabled={isFirst}
                        style={{ background: 'none', border: 'none', padding: '1px 3px', cursor: isFirst ? 'default' : 'pointer', color: isFirst ? '#d1d5db' : '#6b7280', lineHeight: 1 }}>
                        <i className="feather-chevron-up" style={{ fontSize: '12px' }} />
                    </button>
                    <button onClick={() => onMoveDown(stage.Asama_ID)} disabled={isLast}
                        style={{ background: 'none', border: 'none', padding: '1px 3px', cursor: isLast ? 'default' : 'pointer', color: isLast ? '#d1d5db' : '#6b7280', lineHeight: 1 }}>
                        <i className="feather-chevron-down" style={{ fontSize: '12px' }} />
                    </button>
                </div>
                <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: stage.Renk, flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: '13px', fontWeight: 600, color: '#333' }}>{stage.Ad}</span>
                {/* Badges */}
                <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '8px', background: ti.color + '20', color: ti.color }}>
                        {ti.label}
                    </span>
                    {stage.Hatirlatma_Gun && (
                        <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '8px', background: '#fef3c7', color: '#92400e' }} title="Otomatik hatırlatma">
                            <i className="feather-bell" style={{ fontSize: '9px', marginRight: '2px' }}></i>{stage.Hatirlatma_Gun}g
                        </span>
                    )}
                    <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '8px', background: '#ecfdf5', color: '#047857' }} title="Kapanma İhtimali">
                        %{stage.Kapanma_Yuzdesi ?? 0}
                    </span>
                    {stageAltList.length > 0 && (
                        <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '8px', background: '#eff6ff', color: '#1d4ed8' }}>
                            {stageAltList.length} seçenek
                        </span>
                    )}
                </div>
                <button className="btn btn-sm btn-light py-0 px-2" onClick={() => setEditing(e => !e)} style={{ fontSize: '12px' }}>
                    <i className={editing ? 'feather-chevron-up' : 'feather-edit-2'} />
                </button>
                <button className="btn btn-sm btn-light py-0 px-2" onClick={() => onDelete(stage.Asama_ID)}
                    style={{ fontSize: '12px', color: '#ef4444' }}>
                    <i className="feather-trash-2" />
                </button>
            </div>

            {/* ── Düzenleme paneli ── */}
            {editing && (
                <div style={{ padding: '12px 14px 14px', borderTop: '1px solid #f0f0f0', background: '#fafafa', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {/* Ad + renk */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input type="text" className="form-control form-control-sm" value={ad}
                            onChange={e => setAd(e.target.value)} style={{ fontSize: '13px', borderRadius: '6px', flex: 1 }} />
                        <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                            {COLORS.map(c => (
                                <div key={c} onClick={() => setRenk(c)}
                                    style={{ width: '18px', height: '18px', borderRadius: '4px', background: c, cursor: 'pointer', border: renk === c ? '2px solid #111' : '2px solid transparent' }} />
                            ))}
                        </div>
                    </div>

                    {/* Tür + Hatırlatma */}
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '11px', fontWeight: 700, color: '#555', marginBottom: '4px' }}>Aşama Türü</div>
                            <select value={tur} onChange={e => setTur(e.target.value)}
                                className="form-select form-select-sm" style={{ fontSize: '12px', borderRadius: '6px' }}>
                                {TUR_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>
                        <div style={{ width: '140px' }}>
                            <div style={{ fontSize: '11px', fontWeight: 700, color: '#555', marginBottom: '4px' }}>
                                Hatırlatma (iş günü)
                                <span style={{ fontWeight: 400, color: '#aaa', marginLeft: '4px' }}>opsiyonel</span>
                            </div>
                            <input type="number" min="1" max="30" className="form-control form-control-sm"
                                value={hatirlatmaGun} onChange={e => setHatirlatmaGun(e.target.value)}
                                placeholder="örn: 3" style={{ fontSize: '12px', borderRadius: '6px' }} />
                        </div>
                        <div style={{ width: '140px' }}>
                            <div style={{ fontSize: '11px', fontWeight: 700, color: '#555', marginBottom: '4px' }}>
                                Kapanma İhtimali (%)
                            </div>
                            <input type="number" min="0" max="100" className="form-control form-control-sm"
                                value={kapanmaYuzdesi} onChange={e => setKapanmaYuzdesi(e.target.value)}
                                placeholder="örn: 30" style={{ fontSize: '12px', borderRadius: '6px' }} />
                        </div>
                    </div>

                    {/* Alt Aksiyonlar */}
                    <div>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: '#555', marginBottom: '6px' }}>
                            Alt Aksiyonlar / Sonuçlar
                            <span style={{ fontWeight: 400, color: '#aaa', marginLeft: '4px' }}>opsiyonel — ör: "Ulaşıldı", "Ulaşılamadı"</span>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '6px' }}>
                            {altAksiyonlar.map(v => (
                                <span key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '8px', background: '#e0f2fe', color: '#0369a1' }}>
                                    {v}
                                    <button type="button" onClick={() => removeAltAksiyon(v)}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 0, fontSize: '12px', lineHeight: 1 }}>×</button>
                                </span>
                            ))}
                        </div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                            <input type="text" className="form-control form-control-sm" value={altAksiyonlarInput}
                                onChange={e => setAltAksiyonlarInput(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && addAltAksiyon()}
                                placeholder="Seçenek yaz ve Enter'a bas"
                                style={{ fontSize: '12px', borderRadius: '6px', flex: 1 }} />
                            <button type="button" className="btn btn-sm btn-outline-primary" onClick={addAltAksiyon}
                                style={{ fontSize: '12px', borderRadius: '6px', flexShrink: 0 }}>Ekle</button>
                        </div>
                    </div>

                    {/* Kaydet / İptal */}
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                        <button className="btn btn-sm btn-light" onClick={() => setEditing(false)} style={{ fontSize: '12px' }}>İptal</button>
                        <button className="btn btn-sm btn-success" onClick={handleSave} disabled={saving} style={{ fontSize: '12px' }}>
                            {saving ? <span className="spinner-border spinner-border-sm" /> : <><i className="feather-save me-1" />Kaydet</>}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Ana Ayarlar Sayfası ───────────────────────────────────────────────────────
const Ayarlar = () => {
    const { refreshSettings, applySettings } = useSettings();
    const [activeTab, setActiveTab] = useState('moduller');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // VAPI sub-tab ve yardımcı state
    const [vapiSubTab, setVapiSubTab] = useState('api');
    const [vapiVoices, setVapiVoices] = useState({ native: {}, elevenLabs: [] });
    const [vapiSyncing, setVapiSyncing] = useState(false);
    const [vapiNewQuestion, setVapiNewQuestion] = useState('');

    // Modül ayarları state'i
    const [modules, setModules] = useState({
        whatsapp: { aktif: false, phoneNumberId: '', businessAccountId: '', accessToken: '', webhookToken: '', firmaId: '' },
        telegram: { aktif: false, botToken: '', chatId: '', groupName: '' },
        netgsm: { aktif: false, kullaniciAdi: '', sifre: '', baslik: '' },
        vapi: {
            aktif: false, apiKey: '', phoneNumberId: '', geminiApiKey: '', elevenLabsApiKey: '',
            assistantId: '', assistantName: '', sirketAdi: '', systemPrompt: '', greetingMessage: '',
            closingMessage: '', qualifyingQuestions: [], voiceProvider: 'openai', voiceId: 'nova', webhookUrl: '',
            language: 'tr', otomatikArama: false, otomatikAramaGecikmeSaniye: 60,
            maxDeneme: 2, programlanmisBaslangic: '09:00', programlanmisBitis: '20:00',
            customScoringInstructions: '',
        },
    });

    // Pipeline state
    const [stages, setStages] = useState([]);
    const [newStageName, setNewStageName] = useState('');
    const [newStageColor, setNewStageColor] = useState('#3b82f6');
    const [newStageTur, setNewStageTur] = useState('normal');
    const [newStageHatirlatmaGun, setNewStageHatirlatmaGun] = useState('');
    const [newStageAltAksiyonlar, setNewStageAltAksiyonlar] = useState([]);
    const [newStageAltInput, setNewStageAltInput] = useState('');
    const [newStageKapanmaYuzdesi, setNewStageKapanmaYuzdesi] = useState('');
    const [addingStage, setAddingStage] = useState(false);
    const [creatingDefault, setCreatingDefault] = useState(false);

    // Genel ayarlar
    const [genel, setGenel] = useState({
        sirketAdi: '',
        timezone: 'Europe/Istanbul',
        defaultLabel: '',
    });

    useEffect(() => {
        loadAll();
    }, []);

    const loadAll = async () => {
        setLoading(true);
        try {
            const [settingsRes, pipelineRes] = await Promise.all([
                get('settings'),
                get('settings/pipeline')
            ]);
            const s = settingsRes?.settings || {};

            // Modülleri doldur
            setModules(prev => {
                const next = { ...prev };
                Object.keys(next).forEach(key => {
                    if (s[`modul_${key}`]) {
                        const saved = s[`modul_${key}`];
                        next[key] = {
                            ...next[key],
                            ...saved,
                            // qualifyingQuestions array korunsun
                            ...(key === 'vapi' && Array.isArray(saved.qualifyingQuestions)
                                ? { qualifyingQuestions: saved.qualifyingQuestions }
                                : {}),
                            // Azure artık VAPI tarafından desteklenmiyor, openai'ya geç
                            ...(key === 'vapi' && saved.voiceProvider === 'azure'
                                ? { voiceProvider: 'openai', voiceId: 'nova' }
                                : {}),
                        };
                    }
                });
                return next;
            });

            // Genel
            if (s.genel) setGenel(g => ({ ...g, ...s.genel }));

            setStages(pipelineRes?.stages || []);

            // VAPI ses listesi
            if (s.modul_vapi?.aktif || s.modul_vapi?.apiKey) {
                try {
                    const voicesRes = await get('vapi/voices');
                    setVapiVoices(voicesRes || { native: {}, elevenLabs: [] });
                } catch { /* voicelist yüklenemedi, önemsiz */ }
            }
        } catch {
            toast.error('Ayarlar yüklenemedi');
        }
        setLoading(false);
    };

    const loadVapiVoices = useCallback(async () => {
        try {
            const res = await get('vapi/voices');
            setVapiVoices(res || { native: {}, elevenLabs: [] });
        } catch { /* sessizce geç */ }
    }, []);

    const handleVapiSync = async () => {
        setVapiSyncing(true);
        try {
            // Önce mevcut VAPI ayarlarını kaydet, sonra senkronize et
            await put('settings', { settings: { modul_vapi: modules.vapi } });
            applySettings({ modul_vapi: modules.vapi });

            const res = await post('vapi/assistant/sync', {});
            toast.success(`Asistan senkronize edildi! ID: ${res.assistantId}`);
            updateModule('vapi', 'assistantId', res.assistantId);
            // assistantId'yi de DB'ye kaydet
            await put('settings', { settings: { modul_vapi: { ...modules.vapi, assistantId: res.assistantId } } });
        } catch (e) {
            const msg = e?.response?.data?.message || e?.message || 'Senkronizasyon başarısız';
            toast.error(msg);
            console.error('[VAPI Sync]', e?.response?.data || e);
        }
        setVapiSyncing(false);
    };

    const addVapiQuestion = () => {
        if (!vapiNewQuestion.trim()) return;
        setModules(prev => ({
            ...prev,
            vapi: { ...prev.vapi, qualifyingQuestions: [...(prev.vapi.qualifyingQuestions || []), vapiNewQuestion.trim()] }
        }));
        setVapiNewQuestion('');
    };

    const removeVapiQuestion = (idx) => {
        setModules(prev => ({
            ...prev,
            vapi: { ...prev.vapi, qualifyingQuestions: prev.vapi.qualifyingQuestions.filter((_, i) => i !== idx) }
        }));
    };

    const handleSaveModules = async () => {
        setSaving(true);
        try {
            const settingsPayload = {};
            Object.keys(modules).forEach(key => {
                settingsPayload[`modul_${key}`] = modules[key];
            });
            settingsPayload.genel = genel;
            await put('settings', { settings: settingsPayload });
            // Anında context'i güncelle — menü hemen değişir
            applySettings(settingsPayload);
            // Sunucudan da tazele (arka planda)
            refreshSettings();
            toast.success('Ayarlar kaydedildi!');
        } catch {
            toast.error('Kayıt başarısız');
        }
        setSaving(false);
    };

    const toggleModule = (name) => {
        setModules(prev => ({
            ...prev,
            [name]: { ...prev[name], aktif: !prev[name].aktif }
        }));
    };

    const updateModule = (name, field, value) => {
        setModules(prev => ({
            ...prev,
            [name]: { ...prev[name], [field]: value }
        }));
    };

    // Pipeline işlemleri
    const handleAddStage = async () => {
        if (!newStageName.trim()) return;
        setAddingStage(true);
        try {
            const maxSira = stages.length ? Math.max(...stages.map(s => s.Sira)) + 1 : 0;
            await post('settings/pipeline', {
                ad: newStageName.trim(), renk: newStageColor, sira: maxSira,
                tur: newStageTur,
                hatirlatma_gun: newStageHatirlatmaGun ? parseInt(newStageHatirlatmaGun) : null,
                alt_aksiyonlar: newStageAltAksiyonlar.length ? newStageAltAksiyonlar : null,
                kapanma_yuzdesi: newStageKapanmaYuzdesi ? parseInt(newStageKapanmaYuzdesi) : 0
            });
            toast.success('Aşama eklendi');
            setNewStageName('');
            setNewStageColor('#3b82f6');
            setNewStageTur('normal');
            setNewStageHatirlatmaGun('');
            setNewStageAltAksiyonlar([]);
            setNewStageAltInput('');
            setNewStageKapanmaYuzdesi('');
            const r = await get('settings/pipeline');
            setStages(r?.stages || []);
        } catch {
            toast.error('Aşama eklenemedi');
        }
        setAddingStage(false);
    };

    const handleCreateDefaultPipeline = async () => {
        if (!window.confirm('Varsayılan satış pipeline\'ı oluşturmak istiyor musunuz? Mevcut aşamaları silmez, yeni aşamalar ekler.')) return;
        setCreatingDefault(true);
        try {
            await post('settings/pipeline/default', {});
            toast.success('Varsayılan pipeline oluşturuldu!');
            const r = await get('settings/pipeline');
            setStages(r?.stages || []);
        } catch {
            toast.error('Pipeline oluşturulamadı');
        }
        setCreatingDefault(false);
    };

    const handleUpdateStage = async (id, data) => {
        try {
            await put(`settings/pipeline/${id}`, data);
            const r = await get('settings/pipeline');
            setStages(r?.stages || []);
            toast.success('Aşama güncellendi');
        } catch {
            toast.error('Güncelleme başarısız');
        }
    };

    const handleDeleteStage = async (id) => {
        if (!window.confirm('Bu aşamayı silmek istediğinize emin misiniz?')) return;
        try {
            await del(`settings/pipeline/${id}`);
            setStages(prev => prev.filter(s => s.Asama_ID !== id));
            toast.success('Aşama silindi');
        } catch {
            toast.error('Silme başarısız');
        }
    };

    const handleMoveStage = async (id, direction) => {
        const sorted = [...stages].sort((a, b) => a.Sira - b.Sira);
        const idx = sorted.findIndex(s => s.Asama_ID === id);
        const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
        if (swapIdx < 0 || swapIdx >= sorted.length) return;
        const newOrder = sorted.map((s, i) => {
            if (i === idx) return { id: s.Asama_ID, sira: sorted[swapIdx].Sira };
            if (i === swapIdx) return { id: s.Asama_ID, sira: sorted[idx].Sira };
            return { id: s.Asama_ID, sira: s.Sira };
        });
        setStages(prev => prev.map(s => {
            const found = newOrder.find(o => o.id === s.Asama_ID);
            return found ? { ...s, Sira: found.sira } : s;
        }));
        try {
            await put('settings/pipeline/reorder', { order: newOrder });
        } catch {
            toast.error('Sıralama kaydedilemedi');
            const r = await get('settings/pipeline');
            setStages(r?.stages || []);
        }
    };

    // Özellik bayrakları state'i
    const [featureFlags, setFeatureFlags] = useState({
        pipeline: true,
        call_count: true,
        visit_date: true,
        meet_count: true,
        utm_info: true,
        vapi_tab: true,
        sms: true,
        meeting_info: true,
        teklif_date: true,
        kayip_geri_al: true,
    });

    useEffect(() => {
        const s = {};
        get('settings').then(r => {
            const flags = r?.settings?.feature_flags;
            if (flags && typeof flags === 'object') {
                setFeatureFlags(prev => ({ ...prev, ...flags }));
            }
        }).catch(() => {});
    }, []);

    const toggleFeature = (key) => {
        setFeatureFlags(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleSaveFeatures = async () => {
        setSaving(true);
        try {
            await put('settings', { settings: { feature_flags: featureFlags } });
            applySettings({ feature_flags: featureFlags });
            refreshSettings();
            toast.success('Özellik ayarları kaydedildi!');
        } catch {
            toast.error('Kayıt başarısız');
        }
        setSaving(false);
    };

    const FEATURE_LIST = [
        { key: 'pipeline', label: 'Pipeline Görünümü', description: 'Lead detay sayfasındaki pipeline ilerleme çubuğu', icon: 'feather-trending-up', color: '#6366f1' },
        { key: 'call_count', label: 'Arama Sayısı', description: 'Manuel arama sayısı takip alanı', icon: 'feather-phone-call', color: '#0ea5e9' },
        { key: 'visit_date', label: 'Tel/Mail Görüşme Zamanı', description: 'Telefon veya mail görüşmesi tarihi alanı', icon: 'feather-headphones', color: '#10b981' },
        { key: 'meet_count', label: 'Görüşme Sayısı', description: 'Kaç kez görüşme yapıldığı alanı', icon: 'feather-users', color: '#f59e0b' },
        { key: 'meeting_info', label: 'Toplantı Bilgileri', description: 'Toplantı talep ve gerçekleşme tarihleri', icon: 'feather-calendar', color: '#8b5cf6' },
        { key: 'teklif_date', label: 'Teklif Tarihi', description: 'Teklif iletme tarihi alanı', icon: 'feather-send', color: '#f97316' },
        { key: 'sms', label: 'SMS Gönder', description: 'Lead detayında SMS gönderme butonu', icon: 'feather-message-square', color: '#06b6d4' },
        { key: 'vapi_tab', label: 'AI Aramalar Sekmesi', description: 'VAPI ile yapılan AI aramaları sekmesi', icon: 'feather-phone-call', color: '#7c3aed' },
        { key: 'utm_info', label: 'UTM & Reklam Bilgileri', description: 'UTM parametreleri ve reklam bilgileri bölümü', icon: 'feather-trending-up', color: '#64748b' },
    ];

    const tabs = [
        { id: 'moduller', label: 'Modüller', icon: 'feather-grid' },
        { id: 'pipeline', label: 'Satış Pipeline', icon: 'feather-trending-up' },
        { id: 'ozellikler', label: 'Özellikler', icon: 'feather-sliders' },
        { id: 'genel', label: 'Genel', icon: 'feather-settings' },
    ];

    return (
        <Layout>
            <main className="nxl-container">
                <div className="nxl-content">
                    {/* Sayfa başlığı */}
                    <div className="page-header" style={{ paddingBottom: '12px' }}>
                        <div className="page-header-left d-flex align-items-center gap-3">
                            <div style={{ width: '38px', height: '38px', background: '#6366f1', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="feather-settings text-white" style={{ fontSize: '18px' }}></i>
                            </div>
                            <div>
                                <h5 className="mb-0 fw-bold">Ayarlar</h5>
                                <div className="text-muted" style={{ fontSize: '12px' }}>Sistem yapılandırması ve entegrasyonlar</div>
                            </div>
                        </div>
                    </div>

                    <div className="main-content">
                        {loading ? (
                            <div className="d-flex align-items-center justify-content-center py-5">
                                <span className="spinner-border text-primary me-2"></span>
                                <span className="text-muted">Yükleniyor...</span>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                                {/* Sol Sekme Menüsü */}
                                <div style={{ width: '200px', flexShrink: 0 }}>
                                    <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                                        {tabs.map(tab => (
                                            <button
                                                key={tab.id}
                                                onClick={() => setActiveTab(tab.id)}
                                                style={{
                                                    width: '100%', padding: '12px 16px', border: 'none', borderLeft: `3px solid ${activeTab === tab.id ? '#6366f1' : 'transparent'}`,
                                                    background: activeTab === tab.id ? '#f0f0ff' : '#fff',
                                                    color: activeTab === tab.id ? '#6366f1' : '#555',
                                                    fontWeight: activeTab === tab.id ? 700 : 500,
                                                    fontSize: '13px', cursor: 'pointer', textAlign: 'left',
                                                    display: 'flex', alignItems: 'center', gap: '8px',
                                                    borderBottom: '1px solid #f5f5f5'
                                                }}>
                                                <i className={tab.icon} style={{ fontSize: '15px' }}></i>
                                                {tab.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* İçerik Alanı */}
                                <div style={{ flex: 1 }}>

                                    {/* ── MODÜLLER SEKMESİ ─────────────────────────── */}
                                    {activeTab === 'moduller' && (
                                        <div>
                                            <div style={{ marginBottom: '20px' }}>
                                                <h6 className="fw-bold mb-1">Entegrasyon Modülleri</h6>
                                                <p style={{ fontSize: '13px', color: '#888', margin: 0 }}>
                                                    Kullanmak istediğiniz modülleri etkinleştirin ve yapılandırın.
                                                </p>
                                            </div>

                                            {/* WhatsApp */}
                                            <ModuleCard
                                                icon="bi bi-whatsapp"
                                                iconBg="#25D366"
                                                title="WhatsApp Business"
                                                description="Meta Business API ile WhatsApp mesajlaşma"
                                                active={modules.whatsapp.aktif}
                                                onToggle={() => toggleModule('whatsapp')}
                                            >
                                                <div className="row g-3">
                                                    <div className="col-md-6">
                                                        <Field
                                                            label="Phone Number ID"
                                                            value={modules.whatsapp.phoneNumberId}
                                                            onChange={v => updateModule('whatsapp', 'phoneNumberId', v)}
                                                            placeholder="123456789012345"
                                                            help="Meta Business Manager → WhatsApp → Phone Number ID"
                                                        />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <Field
                                                            label="Business Account ID (WABA ID)"
                                                            value={modules.whatsapp.businessAccountId}
                                                            onChange={v => updateModule('whatsapp', 'businessAccountId', v)}
                                                            placeholder="123456789012345"
                                                        />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <Field
                                                            label="Access Token"
                                                            value={modules.whatsapp.accessToken}
                                                            onChange={v => updateModule('whatsapp', 'accessToken', v)}
                                                            placeholder="EAAxxxxx..."
                                                            password
                                                            help="Meta Developers → Your App → Access Token"
                                                        />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <Field
                                                            label="Webhook Verify Token"
                                                            value={modules.whatsapp.webhookToken}
                                                            onChange={v => updateModule('whatsapp', 'webhookToken', v)}
                                                            placeholder="gizli_token"
                                                            password
                                                            help="Webhook doğrulama için belirlediğiniz token"
                                                        />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <Field
                                                            label="Firma ID (Varsayılan)"
                                                            value={modules.whatsapp.firmaId}
                                                            onChange={v => updateModule('whatsapp', 'firmaId', v)}
                                                            placeholder="1"
                                                            help="Yeni leadler bu firmaya atanır"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="mt-2 p-3 rounded" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: '12px', color: '#166534' }}>
                                                    <i className="feather-info me-1"></i>
                                                    Webhook URL: <code style={{ background: '#dcfce7', padding: '1px 5px', borderRadius: '4px' }}>https://levelupapi.levelupcrm.com.tr/whatsapp/webhook</code>
                                                </div>
                                            </ModuleCard>

                                            {/* Telegram */}
                                            <ModuleCard
                                                icon="bi bi-telegram"
                                                iconBg="#0088cc"
                                                title="Telegram"
                                                description="Telegram Bot API ile bildirim ve mesajlaşma"
                                                active={modules.telegram.aktif}
                                                onToggle={() => toggleModule('telegram')}
                                            >
                                                <div className="row g-3">
                                                    <div className="col-md-6">
                                                        <Field
                                                            label="Bot Token"
                                                            value={modules.telegram.botToken}
                                                            onChange={v => updateModule('telegram', 'botToken', v)}
                                                            placeholder="1234567890:ABCdefGHIjklMNOpqrSTUvwxYZ"
                                                            password
                                                            help="@BotFather'dan alınan bot token"
                                                        />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <Field
                                                            label="Chat ID / Grup ID"
                                                            value={modules.telegram.chatId}
                                                            onChange={v => updateModule('telegram', 'chatId', v)}
                                                            placeholder="-1001234567890"
                                                            help="Bildirimlerin gönderileceği grup/kanal ID"
                                                        />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <Field
                                                            label="Grup/Kanal Adı (isteğe bağlı)"
                                                            value={modules.telegram.groupName}
                                                            onChange={v => updateModule('telegram', 'groupName', v)}
                                                            placeholder="Satış Ekibi"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="mt-2 p-3 rounded" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: '12px', color: '#1e40af' }}>
                                                    <i className="feather-info me-1"></i>
                                                    Bot token almak için Telegram'da <strong>@BotFather</strong> ile konuşun ve <code>/newbot</code> komutunu kullanın.
                                                </div>
                                            </ModuleCard>

                                            {/* NetGSM */}
                                            <ModuleCard
                                                icon="feather-message-square"
                                                iconBg="#f59e0b"
                                                title="NetGSM SMS"
                                                description="NetGSM altyapısı ile SMS gönderimi"
                                                active={modules.netgsm.aktif}
                                                onToggle={() => toggleModule('netgsm')}
                                            >
                                                <div className="row g-3">
                                                    <div className="col-md-4">
                                                        <Field
                                                            label="Kullanıcı Adı"
                                                            value={modules.netgsm.kullaniciAdi}
                                                            onChange={v => updateModule('netgsm', 'kullaniciAdi', v)}
                                                            placeholder="NetGSM kullanıcı adı"
                                                        />
                                                    </div>
                                                    <div className="col-md-4">
                                                        <Field
                                                            label="Şifre"
                                                            value={modules.netgsm.sifre}
                                                            onChange={v => updateModule('netgsm', 'sifre', v)}
                                                            placeholder="NetGSM şifreniz"
                                                            password
                                                        />
                                                    </div>
                                                    <div className="col-md-4">
                                                        <Field
                                                            label="Gönderen Başlık"
                                                            value={modules.netgsm.baslik}
                                                            onChange={v => updateModule('netgsm', 'baslik', v)}
                                                            placeholder="FIRMAADI"
                                                            help="Maks 11 karakter, onaylı başlık"
                                                        />
                                                    </div>
                                                </div>
                                            </ModuleCard>

                                            {/* VAPI AI Agent */}
                                            <ModuleCard
                                                icon="feather-phone-call"
                                                iconBg="#7c3aed"
                                                title="VAPI AI Agent"
                                                description="Yapay zeka destekli otomatik sesli arama ve lead nitelendirme"
                                                active={modules.vapi.aktif}
                                                onToggle={() => { toggleModule('vapi'); if (!modules.vapi.aktif) loadVapiVoices(); }}
                                            >
                                                {/* Sub-tab bar */}
                                                <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', background: '#f8f8f8', borderRadius: '8px', padding: '4px' }}>
                                                    {[
                                                        { id: 'api', label: 'API Bağlantıları', icon: 'feather-key' },
                                                        { id: 'ses', label: 'Ses Ayarları', icon: 'feather-volume-2' },
                                                        { id: 'egitim', label: 'Asistan Eğitimi', icon: 'feather-book-open' },
                                                        { id: 'arama', label: 'Arama Parametreleri', icon: 'feather-sliders' },
                                                    ].map(st => (
                                                        <button key={st.id} onClick={() => setVapiSubTab(st.id)}
                                                            style={{
                                                                flex: 1, padding: '6px 8px', border: 'none', borderRadius: '6px', cursor: 'pointer',
                                                                background: vapiSubTab === st.id ? '#7c3aed' : 'transparent',
                                                                color: vapiSubTab === st.id ? '#fff' : '#666',
                                                                fontWeight: 600, fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'
                                                            }}>
                                                            <i className={st.icon} style={{ fontSize: '11px' }}></i>
                                                            {st.label}
                                                        </button>
                                                    ))}
                                                </div>

                                                {/* API Bağlantıları */}
                                                {vapiSubTab === 'api' && (
                                                    <div className="row g-3">
                                                        <div className="col-md-6">
                                                            <Field label="VAPI Private Key" value={modules.vapi.apiKey}
                                                                onChange={v => updateModule('vapi', 'apiKey', v)}
                                                                placeholder="sk-..."  password
                                                                help="dashboard.vapi.ai → Org Settings → API Keys → Private Key (Public Key değil!)" />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <Field label="VAPI Phone Number ID" value={modules.vapi.phoneNumberId}
                                                                onChange={v => updateModule('vapi', 'phoneNumberId', v)}
                                                                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                                                help="dashboard.vapi.ai → Phone Numbers" />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <Field label="Gemini API Key" value={modules.vapi.geminiApiKey}
                                                                onChange={v => updateModule('vapi', 'geminiApiKey', v)}
                                                                placeholder="AIzaSy..." password
                                                                help="aistudio.google.com → Get API Key (ücretsiz)" />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <Field label="ElevenLabs API Key (isteğe bağlı)" value={modules.vapi.elevenLabsApiKey}
                                                                onChange={v => updateModule('vapi', 'elevenLabsApiKey', v)}
                                                                placeholder="sk_..." password
                                                                help="ElevenLabs sesleri için" />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <Field label="Asistan Adı" value={modules.vapi.assistantName}
                                                                onChange={v => updateModule('vapi', 'assistantName', v)}
                                                                placeholder="LevelUp AI Asistan" />
                                                        </div>
                                                        <div className="col-12">
                                                            <Field label="Webhook URL (Arama raporu için)" value={modules.vapi.webhookUrl}
                                                                onChange={v => updateModule('vapi', 'webhookUrl', v)}
                                                                placeholder="https://api.sirketiniz.com/vapi/webhook"
                                                                help="API sunucunuzun public URL'i + /vapi/webhook — transcript ve skor kayıt için zorunlu. Kaydet → Senkronize butonuna basın." />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <div className="mb-0">
                                                                <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>Mevcut Asistan ID</label>
                                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                                    <input className="form-control form-control-sm" readOnly
                                                                        value={modules.vapi.assistantId || ''}
                                                                        placeholder="Henüz senkronize edilmedi"
                                                                        style={{ borderRadius: '8px', fontSize: '13px', background: '#f9f9f9' }} />
                                                                    <button className="btn btn-sm btn-outline-primary px-3"
                                                                        onClick={handleVapiSync} disabled={vapiSyncing || !modules.vapi.apiKey}
                                                                        style={{ borderRadius: '8px', whiteSpace: 'nowrap', fontWeight: 600 }}>
                                                                        {vapiSyncing ? <span className="spinner-border spinner-border-sm" /> : <><i className="feather-refresh-cw me-1"></i>Senkronize</>}
                                                                    </button>
                                                                </div>
                                                                <div style={{ fontSize: '11px', color: '#aaa', marginTop: '3px' }}>Asistan eğitimini kaydettikten sonra senkronize edin</div>
                                                            </div>
                                                        </div>
                                                        <div className="col-12">
                                                            <div className="p-3 rounded" style={{ background: '#faf5ff', border: '1px solid #e9d5ff', fontSize: '12px', color: '#6b21a8' }}>
                                                                <i className="feather-info me-1"></i>
                                                                Webhook URL'ini VAPI panelinde ayarlayın: <code style={{ background: '#ede9fe', padding: '1px 5px', borderRadius: '4px' }}>https://levelupapi.levelupcrm.com.tr/vapi/webhook</code>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Ses Ayarları */}
                                                {vapiSubTab === 'ses' && (
                                                    <div className="row g-3">
                                                        <div className="col-md-4">
                                                            <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>Ses Sağlayıcı</label>
                                                            <select className="form-select form-select-sm"
                                                                value={modules.vapi.voiceProvider || 'openai'}
                                                                onChange={e => updateModule('vapi', 'voiceProvider', e.target.value)}
                                                                style={{ borderRadius: '8px', fontSize: '13px' }}>
                                                                <option value="openai">OpenAI (Çok Dilli) ⭐</option>
                                                                <option value="deepgram">Deepgram (İngilizce)</option>
                                                                <option value="11labs">ElevenLabs</option>
                                                            </select>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>Ses</label>
                                                            <select className="form-select form-select-sm"
                                                                value={modules.vapi.voiceId || ''}
                                                                onChange={e => updateModule('vapi', 'voiceId', e.target.value)}
                                                                style={{ borderRadius: '8px', fontSize: '13px' }}>
                                                                {modules.vapi.voiceProvider === '11labs'
                                                                    ? (vapiVoices.elevenLabs || []).map(v => <option key={v.id} value={v.id}>{v.label}</option>)
                                                                    : (vapiVoices.native?.[modules.vapi.voiceProvider] || []).map(v => <option key={v.id} value={v.id}>{v.label}</option>)
                                                                }
                                                            </select>
                                                            {(modules.vapi.voiceProvider === '11labs' && !vapiVoices.elevenLabs?.length) && (
                                                                <div style={{ fontSize: '11px', color: '#f59e0b', marginTop: '3px' }}>ElevenLabs API Key girilmeli ve ayarlar kaydedilmeli</div>
                                                            )}
                                                        </div>
                                                        <div className="col-md-4">
                                                            <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>Dil (Transcriber)</label>
                                                            <select className="form-select form-select-sm"
                                                                value={modules.vapi.language || 'tr'}
                                                                onChange={e => updateModule('vapi', 'language', e.target.value)}
                                                                style={{ borderRadius: '8px', fontSize: '13px' }}>
                                                                <option value="tr">Türkçe (tr)</option>
                                                                <option value="en">İngilizce (en)</option>
                                                                <option value="de">Almanca (de)</option>
                                                                <option value="fr">Fransızca (fr)</option>
                                                            </select>
                                                        </div>
                                                        <div className="col-12">
                                                            <div className="p-2 rounded" style={{ background: '#f8f9fa', fontSize: '12px', color: '#666' }}>
                                                                <i className="feather-info me-1"></i>
                                                                Ses değişikliği sonrasında <strong>Asistan Senkronize</strong> butonuna basın.
                                                                {modules.vapi.voiceProvider === '11labs' && (
                                                                    <span className="ms-1">ElevenLabs sesleri yüklemek için önce API Key'i kaydedin.</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Asistan Eğitimi */}
                                                {vapiSubTab === 'egitim' && (
                                                    <div className="row g-3">
                                                        <div className="col-md-6">
                                                            <Field label="Şirket Adı (Agent'ın tanıtacağı)" value={modules.vapi.sirketAdi}
                                                                onChange={v => updateModule('vapi', 'sirketAdi', v)}
                                                                placeholder="LevelUp CRM" />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <Field label="Karşılama Mesajı (firstMessage)" value={modules.vapi.greetingMessage}
                                                                onChange={v => updateModule('vapi', 'greetingMessage', v)}
                                                                placeholder="Merhaba! Şirketimiz adına arıyorum..." />
                                                        </div>
                                                        <div className="col-12">
                                                            <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>Şirket / Hizmet Bilgi Tabanı (System Prompt)</label>
                                                            <textarea className="form-control form-control-sm" rows={5}
                                                                value={modules.vapi.systemPrompt || ''}
                                                                onChange={e => updateModule('vapi', 'systemPrompt', e.target.value)}
                                                                placeholder="Şirketinizin sunduğu hizmetleri, fiyat aralıklarını, hedef kitlesini ve diğer bilgileri buraya yazın. AI Agent bu bilgileri kullanarak müşterilerle konuşacak."
                                                                style={{ borderRadius: '8px', fontSize: '13px', resize: 'vertical' }} />
                                                        </div>
                                                        <div className="col-12">
                                                            <Field label="Kapanış Talimatı" value={modules.vapi.closingMessage}
                                                                onChange={v => updateModule('vapi', 'closingMessage', v)}
                                                                placeholder="İlgili görünüyorsa: 'Uzmanımız en kısa sürede iletişime geçecek.' de ve kapat." />
                                                        </div>
                                                        <div className="col-12">
                                                            <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '6px', display: 'block' }}>Nitelik Soruları</label>
                                                            {(modules.vapi.qualifyingQuestions || []).map((q, idx) => (
                                                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                                                    <span style={{ width: '20px', height: '20px', background: '#7c3aed', color: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, flexShrink: 0 }}>{idx + 1}</span>
                                                                    <span style={{ flex: 1, fontSize: '13px', color: '#333', background: '#f5f3ff', padding: '6px 10px', borderRadius: '8px' }}>{q}</span>
                                                                    <button className="btn btn-sm py-0 px-2" onClick={() => removeVapiQuestion(idx)}
                                                                        style={{ color: '#ef4444', background: 'none', border: 'none', fontSize: '16px', lineHeight: 1 }}>×</button>
                                                                </div>
                                                            ))}
                                                            <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                                                                <input className="form-control form-control-sm" value={vapiNewQuestion}
                                                                    onChange={e => setVapiNewQuestion(e.target.value)}
                                                                    onKeyDown={e => e.key === 'Enter' && addVapiQuestion()}
                                                                    placeholder="Yeni soru ekle (Enter veya + butonu)"
                                                                    style={{ borderRadius: '8px', fontSize: '13px' }} />
                                                                <button className="btn btn-sm btn-outline-primary px-3" onClick={addVapiQuestion}
                                                                    style={{ borderRadius: '8px', flexShrink: 0 }}>
                                                                    <i className="feather-plus"></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Arama Parametreleri */}
                                                {vapiSubTab === 'arama' && (
                                                    <div className="row g-3">
                                                        <div className="col-12">
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: modules.vapi.otomatikArama ? '#f0fdf4' : '#f9f9f9', border: `1px solid ${modules.vapi.otomatikArama ? '#bbf7d0' : '#e5e7eb'}`, borderRadius: '10px' }}>
                                                                <div>
                                                                    <div style={{ fontWeight: 700, fontSize: '14px', color: '#111' }}>Otomatik Arama</div>
                                                                    <div style={{ fontSize: '12px', color: '#888' }}>Form geldikten belirli süre sonra otomatik arasın</div>
                                                                </div>
                                                                <div onClick={() => updateModule('vapi', 'otomatikArama', !modules.vapi.otomatikArama)}
                                                                    style={{ width: '46px', height: '24px', borderRadius: '12px', background: modules.vapi.otomatikArama ? '#10b981' : '#d1d5db', position: 'relative', transition: 'background 0.2s', cursor: 'pointer', flexShrink: 0 }}>
                                                                    <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '3px', left: modules.vapi.otomatikArama ? '25px' : '3px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>Form Geldikten Sonra Bekleme (saniye)</label>
                                                            <input type="number" className="form-control form-control-sm" min={10} max={3600}
                                                                value={modules.vapi.otomatikAramaGecikmeSaniye || 60}
                                                                onChange={e => updateModule('vapi', 'otomatikAramaGecikmeSaniye', parseInt(e.target.value, 10))}
                                                                style={{ borderRadius: '8px', fontSize: '13px' }} />
                                                            <div style={{ fontSize: '11px', color: '#aaa', marginTop: '3px' }}>Örn: 60 = 1 dakika sonra ara</div>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>Arama Saati Başlangıç</label>
                                                            <input type="time" className="form-control form-control-sm"
                                                                value={modules.vapi.programlanmisBaslangic || '09:00'}
                                                                onChange={e => updateModule('vapi', 'programlanmisBaslangic', e.target.value)}
                                                                style={{ borderRadius: '8px', fontSize: '13px' }} />
                                                        </div>
                                                        <div className="col-md-4">
                                                            <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>Arama Saati Bitiş</label>
                                                            <input type="time" className="form-control form-control-sm"
                                                                value={modules.vapi.programlanmisBitis || '20:00'}
                                                                onChange={e => updateModule('vapi', 'programlanmisBitis', e.target.value)}
                                                                style={{ borderRadius: '8px', fontSize: '13px' }} />
                                                        </div>
                                                        <div className="col-md-4">
                                                            <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>Maks. Deneme Sayısı</label>
                                                            <input type="number" className="form-control form-control-sm" min={1} max={5}
                                                                value={modules.vapi.maxDeneme || 2}
                                                                onChange={e => updateModule('vapi', 'maxDeneme', parseInt(e.target.value, 10))}
                                                                style={{ borderRadius: '8px', fontSize: '13px' }} />
                                                        </div>
                                                        <div className="col-12">
                                                            <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>Özel Skorlama Talimatları (Gemini)</label>
                                                            <textarea className="form-control form-control-sm" rows={3}
                                                                value={modules.vapi.customScoringInstructions || ''}
                                                                onChange={e => updateModule('vapi', 'customScoringInstructions', e.target.value)}
                                                                placeholder="Opsiyonel: Gemini'ye özel değerlendirme kriterleri verin. Örn: 'Bütçesi 10.000 TL üzeri ise bütçe_skoru 20+ ver'"
                                                                style={{ borderRadius: '8px', fontSize: '13px', resize: 'vertical' }} />
                                                        </div>
                                                    </div>
                                                )}
                                            </ModuleCard>

                                            <div className="d-flex justify-content-end mt-2">
                                                <button
                                                    className="btn btn-primary px-4"
                                                    onClick={handleSaveModules}
                                                    disabled={saving}
                                                    style={{ borderRadius: '10px', fontWeight: 600 }}>
                                                    {saving ? <><span className="spinner-border spinner-border-sm me-2"></span>Kaydediliyor...</> : <><i className="feather-save me-2"></i>Ayarları Kaydet</>}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* ── PİPELINE SEKMESİ ─────────────────────────── */}
                                    {activeTab === 'pipeline' && (
                                        <div>
                                            <div className="d-flex align-items-start justify-content-between mb-3">
                                                <div>
                                                    <h6 className="fw-bold mb-1">Satış Pipeline Aşamaları</h6>
                                                    <p style={{ fontSize: '13px', color: '#888', margin: 0 }}>
                                                        Satış sürecinizi özelleştirin. Her aşamaya tür, hatırlatma ve alt aksiyonlar tanımlayabilirsiniz.
                                                    </p>
                                                </div>
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={handleCreateDefaultPipeline}
                                                    disabled={creatingDefault}
                                                    style={{ fontSize: '12px', flexShrink: 0, borderRadius: '8px' }}>
                                                    {creatingDefault
                                                        ? <span className="spinner-border spinner-border-sm" />
                                                        : <><i className="feather-zap me-1"></i>Varsayılan Pipeline Oluştur</>}
                                                </button>
                                            </div>

                                            {/* Tür açıklamaları */}
                                            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                                                {TUR_OPTIONS.map(t => (
                                                    <span key={t.value} style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '8px', background: t.color + '18', color: t.color, fontWeight: 600 }}>
                                                        {t.label}
                                                        {t.value === 'teklif' && <span style={{ fontWeight: 400, marginLeft: '4px' }}>— hatırlatma oluşturur</span>}
                                                        {t.value === 'kaybedildi' && <span style={{ fontWeight: 400, marginLeft: '4px' }}>— kapanış sebebi ister</span>}
                                                    </span>
                                                ))}
                                            </div>

                                            {/* Aşama listesi */}
                                            <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #e5e7eb', padding: '20px', marginBottom: '20px' }}>
                                                {stages.length === 0 ? (
                                                    <div className="text-center py-4">
                                                        <i className="feather-inbox" style={{ fontSize: '32px', color: '#ddd', display: 'block', marginBottom: '8px' }}></i>
                                                        <div style={{ color: '#aaa', fontSize: '13px', marginBottom: '10px' }}>Henüz aşama eklenmemiş</div>
                                                        <button className="btn btn-sm btn-primary" onClick={handleCreateDefaultPipeline} disabled={creatingDefault} style={{ borderRadius: '8px', fontSize: '12px' }}>
                                                            {creatingDefault ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="feather-zap me-1"></i>}
                                                            Varsayılan Pipeline ile Başla
                                                        </button>
                                                    </div>
                                                ) : (
                                                    [...stages].sort((a, b) => a.Sira - b.Sira).map((stage, idx, arr) => (
                                                        <StageRow
                                                            key={stage.Asama_ID}
                                                            stage={stage}
                                                            onSave={handleUpdateStage}
                                                            onDelete={handleDeleteStage}
                                                            onMoveUp={() => handleMoveStage(stage.Asama_ID, 'up')}
                                                            onMoveDown={() => handleMoveStage(stage.Asama_ID, 'down')}
                                                            isFirst={idx === 0}
                                                            isLast={idx === arr.length - 1}
                                                        />
                                                    ))
                                                )}
                                            </div>

                                            {/* Yeni aşama ekle */}
                                            <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #e5e7eb', padding: '20px' }}>
                                                <h6 className="fw-bold mb-3" style={{ fontSize: '14px' }}>Yeni Aşama Ekle</h6>
                                                {/* Ad + Renk */}
                                                <div className="d-flex align-items-end gap-3 mb-3">
                                                    <div style={{ flex: 1 }}>
                                                        <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>Aşama Adı</label>
                                                        <input
                                                            type="text" className="form-control form-control-sm"
                                                            value={newStageName} onChange={e => setNewStageName(e.target.value)}
                                                            onKeyDown={e => e.key === 'Enter' && handleAddStage()}
                                                            placeholder="ör: İlk Görüşme, Teklif Aşaması..."
                                                            style={{ borderRadius: '8px', fontSize: '13px' }} />
                                                    </div>
                                                    <div>
                                                        <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>Renk</label>
                                                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', maxWidth: '220px' }}>
                                                            {COLORS.map(c => (
                                                                <div key={c} onClick={() => setNewStageColor(c)}
                                                                    style={{ width: '22px', height: '22px', borderRadius: '5px', background: c, cursor: 'pointer', border: newStageColor === c ? '3px solid #111' : '3px solid transparent', transition: 'border 0.1s' }} />
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                                {/* Tür + Hatırlatma */}
                                                <div className="d-flex gap-3 mb-3">
                                                    <div style={{ flex: 1 }}>
                                                        <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>Aşama Türü</label>
                                                        <select value={newStageTur} onChange={e => setNewStageTur(e.target.value)}
                                                            className="form-select form-select-sm" style={{ fontSize: '12px', borderRadius: '6px' }}>
                                                            {TUR_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                                        </select>
                                                    </div>
                                                    <div style={{ width: '160px' }}>
                                                        <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>
                                                            Otomatik Hatırlatma <span style={{ fontWeight: 400, color: '#aaa' }}>(iş günü)</span>
                                                        </label>
                                                        <input type="number" min="1" max="30" className="form-control form-control-sm"
                                                            value={newStageHatirlatmaGun} onChange={e => setNewStageHatirlatmaGun(e.target.value)}
                                                            placeholder="ör: 3" style={{ fontSize: '12px', borderRadius: '6px' }} />
                                                    </div>
                                                    <div style={{ width: '160px' }}>
                                                        <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>
                                                            Kapanma İhtimali (%)
                                                        </label>
                                                        <input type="number" min="0" max="100" className="form-control form-control-sm"
                                                            value={newStageKapanmaYuzdesi} onChange={e => setNewStageKapanmaYuzdesi(e.target.value)}
                                                            placeholder="ör: 30" style={{ fontSize: '12px', borderRadius: '6px' }} />
                                                    </div>
                                                </div>
                                                {/* Alt Aksiyonlar */}
                                                <div className="mb-3">
                                                    <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>
                                                        Alt Aksiyonlar / Sonuçlar <span style={{ fontWeight: 400, color: '#aaa' }}>opsiyonel — ör: "Ulaşıldı", "Ulaşılamadı"</span>
                                                    </label>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '6px' }}>
                                                        {newStageAltAksiyonlar.map(v => (
                                                            <span key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '8px', background: '#e0f2fe', color: '#0369a1' }}>
                                                                {v}
                                                                <button type="button" onClick={() => setNewStageAltAksiyonlar(p => p.filter(x => x !== v))}
                                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 0, fontSize: '12px', lineHeight: 1 }}>×</button>
                                                            </span>
                                                        ))}
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '6px' }}>
                                                        <input type="text" className="form-control form-control-sm"
                                                            value={newStageAltInput} onChange={e => setNewStageAltInput(e.target.value)}
                                                            onKeyDown={e => { if (e.key === 'Enter') { const v = newStageAltInput.trim(); if (v && !newStageAltAksiyonlar.includes(v)) setNewStageAltAksiyonlar(p => [...p, v]); setNewStageAltInput(''); } }}
                                                            placeholder="Seçenek yaz ve Enter'a bas" style={{ fontSize: '12px', borderRadius: '6px', flex: 1 }} />
                                                        <button type="button" className="btn btn-sm btn-outline-secondary"
                                                            onClick={() => { const v = newStageAltInput.trim(); if (v && !newStageAltAksiyonlar.includes(v)) setNewStageAltAksiyonlar(p => [...p, v]); setNewStageAltInput(''); }}
                                                            style={{ fontSize: '12px', borderRadius: '6px', flexShrink: 0 }}>Ekle</button>
                                                    </div>
                                                </div>
                                                <button
                                                    className="btn btn-primary"
                                                    onClick={handleAddStage}
                                                    disabled={addingStage || !newStageName.trim()}
                                                    style={{ borderRadius: '8px', fontWeight: 600, height: '34px' }}>
                                                    {addingStage ? <span className="spinner-border spinner-border-sm" /> : <><i className="feather-plus me-1"></i>Aşama Ekle</>}
                                                </button>

                                                {/* Pipeline önizleme */}
                                                {stages.length > 0 && (
                                                    <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
                                                        <div style={{ fontSize: '11px', color: '#888', fontWeight: 600, marginBottom: '8px' }}>PİPELİNE ÖNİZLEME</div>
                                                        <div style={{ display: 'flex', gap: '0', overflowX: 'auto' }}>
                                                            {stages.map((s, i) => (
                                                                <div key={s.Asama_ID} style={{ display: 'flex', alignItems: 'center' }}>
                                                                    <div style={{
                                                                        padding: '6px 16px', background: s.Renk, color: '#fff',
                                                                        fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap',
                                                                        clipPath: i === stages.length - 1
                                                                            ? 'polygon(0 0, 100% 0, 100% 100%, 0 100%)'
                                                                            : 'polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%)',
                                                                        paddingRight: i === stages.length - 1 ? '16px' : '26px',
                                                                        marginRight: '-1px'
                                                                    }}>
                                                                        {s.Tur === 'kazanildi' && <i className="feather-check me-1" style={{ fontSize: '10px' }}></i>}
                                                                        {s.Tur === 'kaybedildi' && <i className="feather-x me-1" style={{ fontSize: '10px' }}></i>}
                                                                        {s.Ad}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* ── ÖZELLİKLER SEKMESİ ──────────────────────── */}
                                    {activeTab === 'ozellikler' && (
                                        <div>
                                            <div style={{ marginBottom: '20px' }}>
                                                <h6 className="fw-bold mb-1">Özellik Yönetimi</h6>
                                                <p style={{ fontSize: '13px', color: '#888', margin: 0 }}>
                                                    Aktif ettiğiniz özellikler lead detay sayfasında görünür, pasif etilenler gizlenir.
                                                </p>
                                            </div>

                                            <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #e5e7eb', overflow: 'hidden', marginBottom: '16px' }}>
                                                {FEATURE_LIST.map((feat, idx) => (
                                                    <div key={feat.key} style={{
                                                        display: 'flex', alignItems: 'center', gap: '14px',
                                                        padding: '14px 20px',
                                                        borderBottom: idx < FEATURE_LIST.length - 1 ? '1px solid #f5f5f5' : 'none',
                                                        background: featureFlags[feat.key] ? '#fff' : '#fafafa'
                                                    }}>
                                                        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: featureFlags[feat.key] ? feat.color : '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.2s' }}>
                                                            <i className={feat.icon} style={{ fontSize: '16px', color: '#fff' }}></i>
                                                        </div>
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ fontWeight: 600, fontSize: '14px', color: featureFlags[feat.key] ? '#111' : '#999' }}>{feat.label}</div>
                                                            <div style={{ fontSize: '12px', color: '#aaa' }}>{feat.description}</div>
                                                        </div>
                                                        <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', userSelect: 'none', flexShrink: 0 }}>
                                                            <span style={{ fontSize: '12px', color: featureFlags[feat.key] ? '#10b981' : '#aaa', fontWeight: 600 }}>
                                                                {featureFlags[feat.key] ? 'Aktif' : 'Pasif'}
                                                            </span>
                                                            <div
                                                                onClick={() => toggleFeature(feat.key)}
                                                                style={{
                                                                    width: '46px', height: '24px', borderRadius: '12px',
                                                                    background: featureFlags[feat.key] ? '#10b981' : '#d1d5db',
                                                                    position: 'relative', transition: 'background 0.2s', cursor: 'pointer'
                                                                }}>
                                                                <div style={{
                                                                    width: '18px', height: '18px', borderRadius: '50%', background: '#fff',
                                                                    position: 'absolute', top: '3px',
                                                                    left: featureFlags[feat.key] ? '25px' : '3px',
                                                                    transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                                                }} />
                                                            </div>
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="d-flex justify-content-end">
                                                <button
                                                    className="btn btn-primary px-4"
                                                    onClick={handleSaveFeatures}
                                                    disabled={saving}
                                                    style={{ borderRadius: '10px', fontWeight: 600 }}>
                                                    {saving ? <><span className="spinner-border spinner-border-sm me-2"></span>Kaydediliyor...</> : <><i className="feather-save me-2"></i>Ayarları Kaydet</>}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* ── GENEL SEKMESİ ────────────────────────────── */}
                                    {activeTab === 'genel' && (
                                        <div>
                                            <div style={{ marginBottom: '20px' }}>
                                                <h6 className="fw-bold mb-1">Genel Ayarlar</h6>
                                                <p style={{ fontSize: '13px', color: '#888', margin: 0 }}>Sistem geneli yapılandırma ayarları</p>
                                            </div>

                                            <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #e5e7eb', padding: '24px' }}>
                                                <div className="row g-3">
                                                    <div className="col-md-6">
                                                        <Field
                                                            label="Şirket Adı"
                                                            value={genel.sirketAdi}
                                                            onChange={v => setGenel(g => ({ ...g, sirketAdi: v }))}
                                                            placeholder="Şirketinizin adı"
                                                        />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>Zaman Dilimi</label>
                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={genel.timezone}
                                                            onChange={e => setGenel(g => ({ ...g, timezone: e.target.value }))}
                                                            style={{ borderRadius: '8px', fontSize: '13px' }}>
                                                            <option value="Europe/Istanbul">Europe/Istanbul (UTC+3)</option>
                                                            <option value="UTC">UTC</option>
                                                            <option value="Europe/London">Europe/London (UTC+0/+1)</option>
                                                            <option value="America/New_York">America/New_York (UTC-5/-4)</option>
                                                        </select>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <Field
                                                            label="Varsayılan Etiket ID"
                                                            value={genel.defaultLabel}
                                                            onChange={v => setGenel(g => ({ ...g, defaultLabel: v }))}
                                                            placeholder="1"
                                                            help="Yeni leadlere otomatik atanacak etiket ID"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="d-flex justify-content-end mt-3">
                                                    <button
                                                        className="btn btn-primary px-4"
                                                        onClick={handleSaveModules}
                                                        disabled={saving}
                                                        style={{ borderRadius: '10px', fontWeight: 600 }}>
                                                        {saving ? <><span className="spinner-border spinner-border-sm me-2"></span>Kaydediliyor...</> : <><i className="feather-save me-2"></i>Kaydet</>}
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Sistem bilgileri */}
                                            <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #e5e7eb', padding: '20px', marginTop: '16px' }}>
                                                <h6 className="fw-bold mb-3" style={{ fontSize: '14px', color: '#555' }}>Sistem Bilgileri</h6>
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                                    {[
                                                        { label: 'Versiyon', value: 'v2.0.0' },
                                                        { label: 'Ortam', value: 'Production' },
                                                        { label: 'API URL', value: 'levelupapi.levelupcrm.com.tr' },
                                                        { label: 'Son Güncelleme', value: new Date().toLocaleDateString('tr-TR') },
                                                    ].map(item => (
                                                        <div key={item.label} style={{ padding: '10px 14px', background: '#f8f9fa', borderRadius: '8px' }}>
                                                            <div style={{ fontSize: '11px', color: '#888', marginBottom: '2px' }}>{item.label}</div>
                                                            <div style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>{item.value}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </Layout>
    );
};

export default Ayarlar;
